import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Ticket, Clock, ArrowLeft, MessageSquare } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [notes, setNotes] = useState({});
  const [trackingInfo, setTrackingInfo] = useState(null);

  useEffect(() => {
    const data = localStorage.getItem('tracked_tickets');
    const info = localStorage.getItem('tracking_info');
    
    if (!data || !info) {
      navigate('/customer/tracking');
      return;
    }
    
    const parsed = JSON.parse(data);
    setTickets(parsed.tickets || []);
    setNotes(parsed.notes || {});
    setTrackingInfo(JSON.parse(info));
  }, [navigate]);

  const handleBack = () => {
    localStorage.removeItem('tracked_tickets');
    localStorage.removeItem('tracking_info');
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'In-Progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'Closed': 'bg-green-100 text-green-800 border-green-200'
    };
    return <Badge className={variants[status] || 'bg-gray-100 text-gray-800'} data-testid={`status-badge-${status.toLowerCase()}`}>{status}</Badge>;
  };

  const getTimeElapsed = (createdAt) => {
    return formatDistanceToNow(new Date(createdAt), { addSuffix: true });
  };

  const getTimeMetrics = (ticket) => {
    const metrics = [];
    if (ticket.time_to_start_hours) {
      metrics.push(`Started in: ${ticket.time_to_start_hours}h`);
    }
    if (ticket.time_to_close_hours) {
      metrics.push(`Resolved in: ${ticket.time_to_close_hours}h`);
    }
    return metrics;
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Ticket className="h-5 w-5 text-accent" />
            <h1 className="text-lg sm:text-2xl font-heading font-bold">My Tickets</h1>
          </div>
          <Button variant="ghost" onClick={handleBack} data-testid="back-home-btn" className="h-11">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {trackingInfo && (
          <Card className="border-border shadow-sm mb-6 bg-accent/5">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Tracking tickets for</p>
                  <p className="font-semibold">{trackingInfo.first_name} — Order ID: {trackingInfo.order_id}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {tickets.length === 0 ? (
          <Card className="border-border shadow-lg text-center py-12">
            <CardContent>
              <Ticket className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2">No tickets found</h3>
              <p className="text-muted-foreground mb-6">No tickets found with this Order ID and Name</p>
              <Button onClick={handleBack} data-testid="go-home-btn">Try Again</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold">Your Support Tickets</h2>
              <Button onClick={() => navigate('/')} data-testid="new-ticket-btn" className="h-11 w-full sm:w-auto">Submit New Ticket</Button>
            </div>

            <div className="grid gap-6" data-testid="tickets-list">
              {tickets.map((ticket) => (
                <Card key={ticket.ticket_id} className="border-border shadow-sm">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-4">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base sm:text-lg font-heading mb-2">{ticket.issue_type}</CardTitle>
                        <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                          <span>Ticket ID: {ticket.ticket_id.substring(0, 8)}</span>
                          <span>•</span>
                          <span>Order: {ticket.order_id}</span>
                        </div>
                      </div>
                      {getStatusBadge(ticket.status)}
                    </div>

                    <div className="bg-muted/50 p-4 rounded-lg mb-4">
                      <p className="text-sm font-semibold mb-2">Your Issue:</p>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{ticket.description}</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>Created {getTimeElapsed(ticket.created_at)}</span>
                      </div>
                      {getTimeMetrics(ticket).length > 0 && (
                        <span className="hidden sm:inline">• {getTimeMetrics(ticket).join(' • ')}</span>
                      )}
                    </div>

                    {notes[ticket.ticket_id] && notes[ticket.ticket_id].length > 0 && (
                      <div className="border-t border-border pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <MessageSquare className="h-4 w-4 text-accent" />
                          <p className="text-sm font-semibold">Updates from Support Team:</p>
                        </div>
                        <div className="space-y-3">
                          {notes[ticket.ticket_id].map((note) => (
                            <div key={note.note_id} className="bg-accent/5 p-3 rounded-lg">
                              <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                              <p className="text-xs text-muted-foreground mt-2">
                                {format(new Date(note.created_at), 'PPpp')}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;
